<template>
  <div class="flex flex-col bg-[#f8fafc] gap-6 overflow-hidden h-full">
    <div class="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169] flex flex-col">
      <div class="px-6 pt-5 pb-1 flex justify-between items-center shrink-0">
        <h2 class="text-base font-bold text-[#0d4169] text-left">Debone Controls</h2>
      </div>

      <form class="px-6 pb-5 pt-2 shrink-0" @submit.prevent>
        <div class="flex flex-wrap items-center gap-x-12 gap-y-4">
          <div class="flex items-center gap-3 min-w-[240px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">Calendar:</label>
            <input
              v-model="currentDate"
              type="date"
              @keydown.prevent
              @change="loadRows"
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
          </div>
          <div class="flex items-center gap-3 min-w-[280px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">Department:</label>
            <select
              v-model="selectedDepartment"
              @change="loadRows"
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
              <option value="" disabled>Select Department</option>
              <option :value="NO_DEPARTMENT">No Department</option>
              <option v-for="dept in activeDepartments" :key="dept.id" :value="String(dept.id)">{{ dept.name }}</option>
            </select>
          </div>
        </div>
      </form>

      <div class="px-6 pb-6 overflow-auto flex-1 custom-scrollbar">
        <div v-if="loading" class="flex items-center justify-center py-16">
          <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <template v-else>
          <table class="w-full debone-count-table min-w-max">
            <thead>
              <tr>
                <th rowspan="2" class="w-10 text-center sticky-col bg-[#f1f5f9] no-side-border">No.</th>
                <th rowspan="2" class="min-w-[120px] sticky-col left-[40px] bg-[#f1f5f9] no-side-border col-ingredient-name">Ingredient Name</th>
                <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Bbf</th>
                <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Received Store</th>
                <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Received Non.Stockable</th>
                <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Product</th>
                <th rowspan="2" class="w-24 text-center">Transfer Recived (D)</th>
                <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Transfer Recived (T)</th>
                <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Wastage</th>
                <th colspan="2" class="text-center bg-slate-100 qty-group-header font-bold">Total On Hand</th>
                <th rowspan="2" class="w-24 text-center">Send To</th>
                <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Total Distributed</th>
                <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Spoiled By Items</th>
                <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Other/Catering</th>
                <th colspan="2" class="text-center bg-blue-50 qty-group-header font-bold">Total Active</th>
                <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Balance</th>
                <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Ending</th>
                <th colspan="2" class="text-center bg-red-50 qty-group-header font-bold text-red-700">Deference</th>
              </tr>
              <tr>
                <template v-for="n in 14" :key="`qty-sub-${n}`">
                  <th class="w-20 text-center bg-[#f1f5f9] qty-sub-header">PC</th>
                  <th class="w-20 text-center bg-[#f1f5f9] qty-sub-header">Kg/L</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in rows"
                :key="row.rowKey || index"
                class="hover:bg-slate-50 transition-colors group"
              >
                <td class="text-center sticky-col bg-white group-hover:bg-slate-50 no-side-border">{{ index + 1 }}</td>
                <td
                  class="sticky-col left-[40px] bg-white group-hover:bg-slate-50 no-side-border font-medium col-ingredient-name"
                  :class="isDeboneKind(row.kind) ? 'text-green-600' : ''"
                >
                  {{ row.ingredientName }}
                </td>
                <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.bbfPc) }}</td>
                <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.bbfKgL) }}</td>
                <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.storeRecvPc) }}</td>
                <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.storeRecvKgL) }}</td>
                <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.nonStockPc) }}</td>
                <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.nonStockKgL) }}</td>
                <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.productPc) }}</td>
                <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.productKgL) }}</td>
                <td>
                  <div v-if="row.transInDetails && row.transInDetails.length > 0" class="flex items-center justify-center">
                    <button
                      type="button"
                      @click.stop="openTransInPopover($event, index)"
                      :disabled="!currentDate || !selectedDepartment || isFutureDate"
                      class="w-full px-2.5 py-0.5 border border-[#cbd5e1] rounded-md outline-none text-xs bg-white cursor-pointer hover:border-[#0d4169] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <span class="text-[#0d4169] font-semibold">Details ▾</span>
                    </button>
                  </div>
                  <div v-else class="text-center text-slate-300">-</div>
                </td>
                <td class="bg-slate-50/50 text-slate-600 text-sm font-bold text-center">{{ displayNum(row.transIn1Pc) }}</td>
                <td class="bg-slate-50/50 text-slate-600 text-sm font-bold text-center">{{ displayNum(row.transIn1KgL) }}</td>
                <td class="col-center">
                  <input
                    v-model.number="row.wastagePc"
                    type="number"
                    min="0"
                    :disabled="deboneControlFieldDisabled(row, 'wastagePc')"
                    @input="recalculateRowAt(index)"
                    @change="recalculateRowAt(index)"
                    class="w-16 mx-auto px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm text-center font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                    :class="deboneControlFieldDisabled(row, 'wastagePc') ? 'bg-slate-100' : 'bg-red-50 focus:bg-white text-slate-700'"
                  >
                </td>
                <td class="col-center">
                  <input
                    v-model.number="row.wastageKgL"
                    type="number"
                    min="0"
                    step="0.01"
                    :disabled="deboneControlFieldDisabled(row, 'wastageKgL')"
                    @input="recalculateRowAt(index)"
                    @change="recalculateRowAt(index)"
                    class="w-16 mx-auto px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm text-center font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                    :class="deboneControlFieldDisabled(row, 'wastageKgL') ? 'bg-slate-100' : 'bg-red-50 focus:bg-white text-slate-700'"
                  >
                </td>
                <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ displayNum(row.totalOnHandPc) }}</td>
                <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ displayNum(row.totalOnHandKgL) }}</td>
                <td>
                  <div class="flex items-center justify-center">
                    <button
                      type="button"
                      @click.stop="openSendToPopover($event, index)"
                      :disabled="isFutureDate"
                      class="w-full px-2.5 py-0.5 border border-[#cbd5e1] rounded-md outline-none text-xs bg-white cursor-pointer text-left flex items-center justify-between gap-1 hover:border-[#0d4169] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <span
                        class="font-black text-center bg-slate-50/30"
                        :class="hasPendingSendTo(row) ? 'text-red-600' : 'text-[#0d4169]'"
                      >{{ hasSendToQuantity(row) ? 'Edit ▾' : 'Send To ▾' }}</span>
                    </button>
                  </div>
                </td>
                <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ displayNum(row.totalDistributedPc) }}</td>
                <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ displayNum(row.totalDistributedKgL) }}</td>
                <td class="col-center">
                  <input
                    v-model.number="row.spoiledItemPc"
                    type="number"
                    min="0"
                    :disabled="deboneControlFieldDisabled(row, 'spoiledItemPc')"
                    @input="recalculateRowAt(index)"
                    @change="recalculateRowAt(index)"
                    class="w-16 mx-auto px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm text-center font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                    :class="deboneControlFieldDisabled(row, 'spoiledItemPc') ? 'bg-slate-100' : 'bg-red-50 focus:bg-white text-slate-700'"
                  >
                </td>
                <td class="col-center">
                  <input
                    v-model.number="row.spoiledItemKgL"
                    type="number"
                    min="0"
                    step="0.01"
                    :disabled="deboneControlFieldDisabled(row, 'spoiledItemKgL')"
                    @input="recalculateRowAt(index)"
                    @change="recalculateRowAt(index)"
                    class="w-16 mx-auto px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm text-center font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                    :class="deboneControlFieldDisabled(row, 'spoiledItemKgL') ? 'bg-slate-100' : 'bg-red-50 focus:bg-white text-slate-700'"
                  >
                </td>
                <td class="col-center">
                  <input
                    v-model.number="row.cateringPc"
                    type="number"
                    min="0"
                    :disabled="deboneControlFieldDisabled(row, 'cateringPc')"
                    @input="recalculateRowAt(index)"
                    @change="recalculateRowAt(index)"
                    class="w-16 mx-auto px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm text-center font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                    :class="deboneControlFieldDisabled(row, 'cateringPc') ? 'bg-slate-100' : 'bg-red-50 focus:bg-white text-slate-700'"
                  >
                </td>
                <td class="col-center">
                  <input
                    v-model.number="row.cateringKgL"
                    type="number"
                    min="0"
                    step="0.01"
                    :disabled="deboneControlFieldDisabled(row, 'cateringKgL')"
                    @input="recalculateRowAt(index)"
                    @change="recalculateRowAt(index)"
                    class="w-16 mx-auto px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm text-center font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                    :class="deboneControlFieldDisabled(row, 'cateringKgL') ? 'bg-slate-100' : 'bg-red-50 focus:bg-white text-slate-700'"
                  >
                </td>
                <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ displayNum(row.totalActivePc) }}</td>
                <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ displayNum(row.totalActiveKgL) }}</td>
                <td class="font-black text-green-700 text-center">{{ displayNum(row.balancePc) }}</td>
                <td class="font-black text-green-700 text-center">{{ displayNum(row.balanceKgL) }}</td>
                <td class="bg-slate-50/50 text-slate-500 text-sm text-center">{{ displayNum(row.endingPc) }}</td>
                <td class="bg-slate-50/50 text-slate-500 text-sm text-center">{{ displayNum(row.endingKgL) }}</td>
                <td
                  class="font-black text-center bg-red-50/30"
                  :class="{ 'text-red-500': Number(row.variancePc) < 0, 'text-green-500': Number(row.variancePc) > 0 }"
                >{{ displayNum(row.variancePc) }}</td>
                <td
                  class="font-black text-center bg-red-50/30"
                  :class="{ 'text-red-500': Number(row.varianceKgL) < 0, 'text-green-500': Number(row.varianceKgL) > 0 }"
                >{{ displayNum(row.varianceKgL) }}</td>
              </tr>
              <tr v-if="rows.length === 0 && currentDate && selectedDepartment">
                <td colspan="32" class="text-center py-12 text-slate-400 italic text-sm">
                  No active deboni ingredients found.
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>

      <div v-if="rows.length > 0 && !loading " class="px-6 py-4 border-t border-slate-200 bg-[#f8fafc] flex justify-center items-center gap-4 shrink-0 rounded-b-xl shadow-[0_-4px_6px_-1px_rgb(0,0,0,0.05)] z-10">
        <button
          type="button"
          @click="openSaveDeboneControlsModal"
          :disabled="!canClickSaveChanges"
          class="px-6 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md hover:bg-[#0a3354] transition-all active:scale-95 shadow-md disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <span v-if="saving" class="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin"></span>
          <span>{{ saving ? 'Saving...' : (isSavedData ? 'Update Changes' : 'Save Changes') }}</span>
        </button>

        <div v-if="saveStatus === 'success'" class="flex items-center gap-1.5 text-emerald-600 font-bold text-sm bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-md">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path></svg>
          {{ isSavedData ? 'Updated Successfully' : 'Saved Successfully' }}
        </div>
        <div v-if="saveStatus === 'error'" class="flex items-center gap-1.5 text-red-600 font-bold text-sm bg-red-50 border border-red-200 px-3 py-1.5 rounded-md">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          Failed to save
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="activePopoverIndex >= 0 && activePopoverKind === 'transIn'"
      :style="popoverStyle"
      class="fixed z-[9999] bg-white border border-[#cbd5e1] rounded-md shadow-xl"
      @click.stop
    >
      <div class="px-3 py-2 text-[11px] font-bold text-[#0d4169] border-b border-slate-100 flex justify-between">
        <span>Incoming Sources</span>
        <span class="text-[10px] text-slate-400">{{ (rows[activePopoverIndex]?.transInDetails || []).length }} dept(s)</span>
      </div>
      <div class="overflow-y-auto custom-scrollbar" :style="{ maxHeight: popoverBodyMaxHeight }">
        <div
          v-for="(det, i) in rows[activePopoverIndex]?.transInDetails || []"
          :key="det.name + i"
          class="flex items-center gap-2 px-3 py-1.5 border-b border-slate-50 last:border-0 hover:bg-slate-50"
        >
          <span class="text-xs font-medium text-slate-700 truncate flex-1">{{ det.name }}</span>
          <span class="text-[10px] font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded whitespace-nowrap">
            PC {{ displayNum(det.qtyPc ?? det.quantity) }} · Kg/L {{ displayNum(det.qtyKgL) }}
          </span>
          <div class="flex items-center gap-1 shrink-0">
            <template v-if="det.is_confirmed === 0">
              <button
                type="button"
                @click.stop="handleConfirmTransfer(activePopoverIndex, i, 1)"
                class="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
              >Accept</button>
              <button
                type="button"
                @click.stop="handleConfirmTransfer(activePopoverIndex, i, 2)"
                class="text-[9px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
              >Reject</button>
            </template>
            <span v-else-if="det.is_confirmed === 1" class="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Confirmed</span>
            <span v-else-if="det.is_confirmed === 2" class="text-[9px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Rejected</span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="activePopoverIndex >= 0 && activePopoverKind === 'sendTo'"
      :style="popoverStyle"
      class="fixed z-[9999] bg-white border border-[#cbd5e1] rounded-md shadow-xl"
      @click.stop
    >
      <div class="px-3 py-2 text-[11px] font-bold text-[#0d4169] border-b border-slate-100 flex justify-between gap-2">
        <span>Issue Quantities</span>
        <span class="text-[10px] font-bold text-[#0d4169] whitespace-nowrap">
          PC {{ displayNum(sendToPopoverTotalPc) }} · Kg/L {{ displayNum(sendToPopoverTotalKgL) }}
        </span>
      </div>
      <div class="pb-10 overflow-y-auto custom-scrollbar" :style="{ maxHeight: popoverBodyMaxHeight }">
        <div
          v-if="sendToDetailsForPopover(rows[activePopoverIndex]).length === 0"
          class="px-3 py-4 text-xs text-slate-400 italic text-center"
        >
          No departments available to issue to. Ensure your user token has a company id and active departments exist for that company.
        </div>
        <div
          v-if="sendToDetailsForPopover(rows[activePopoverIndex]).length > 0"
          class="flex items-center gap-2 px-3 py-1.5 border-b border-slate-200 bg-slate-50 text-[10px] font-bold text-slate-500 uppercase tracking-wide sticky top-0 z-10"
        >
          <span class="flex-1 min-w-0">Department</span>
          <span class="w-16 text-center">PC</span>
          <span class="w-16 text-center">Kg/L</span>
          <span class="w-20 shrink-0"></span>
        </div>
        <div
          v-for="det in sendToDetailsForPopover(rows[activePopoverIndex])"
          :key="det.id"
          class="flex items-center gap-2 px-3 py-1.5 border-b border-slate-50 last:border-0 hover:bg-slate-50"
        >
          <label class="text-xs font-medium text-slate-700 flex-1 truncate min-w-0">{{ det.displayLabel || det.name }}</label>
          <input
            v-model.number="det.qtyPc"
            type="number"
            min="0"
            :disabled="deboneControlFieldDisabled(rows[activePopoverIndex] || {}, 'qtyPc')"
            @input="updateSendToSum(activePopoverIndex)"
            class="w-16 shrink-0 px-1.5 py-1 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs font-bold text-center text-[#0d4169] bg-white transition-all disabled:opacity-60"
            placeholder="PC"
          >
          <input
            v-model.number="det.qtyKgL"
            type="number"
            min="0"
            step="0.01"
            :disabled="deboneControlFieldDisabled(rows[activePopoverIndex] || {}, 'qtyKgL')"
            @input="updateSendToSum(activePopoverIndex)"
            class="w-16 shrink-0 px-1.5 py-1 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs font-bold text-center text-[#0d4169] bg-white transition-all disabled:opacity-60"
            placeholder="Kg/L"
          >
          <span class="w-20 shrink-0 flex justify-center">
            <span
              v-if="isSavedData && (Number(det._savedQtyPc) > 0 || Number(det._savedQtyKgL) > 0)"
              class="text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap"
              :class="det.is_confirmed === 1 ? 'bg-emerald-100 text-emerald-700' : (det.is_confirmed === 2 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700')"
            >
              {{ det.is_confirmed === 1 ? 'Confirmed' : (det.is_confirmed === 2 ? 'Rejected' : 'Not Confirmed') }}
            </span>
          </span>
        </div>
      </div>
    </div>
  </Teleport>

  <div
    v-if="saveDeboneControlsModal.open"
    class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm"
    @click.self="closeSaveDeboneControlsModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all text-left relative overflow-hidden">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
        <h3 class="text-base font-bold m-0">{{ isSavedData ? 'Confirm Update Debone Controls' : 'Confirm Save Debone Controls' }}</h3>
        <button
          type="button"
          class="p-1 hover:bg-white/10 rounded-full transition-colors text-white"
          @click="closeSaveDeboneControlsModal"
          aria-label="Close save modal"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <div class="p-5 space-y-4">
        <div class="text-xs font-semibold text-slate-500">Please enter the code below to confirm that you want to save.</div>

        <div class="px-3 py-2 rounded-md border border-slate-200 bg-green-100 text-[#0d4169] text-sm font-bold tracking-[0.25em] text-center">
          {{ saveDeboneControlsModal.code }}
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b]">Enter Code</label>
          <input
            v-model.trim="saveDeboneControlsModal.input"
            type="text"
            maxlength="4"
            class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm uppercase"
          >
        </div>
        <div v-if="saveDeboneControlsModal.error" class="text-xs text-red-600 font-semibold">{{ saveDeboneControlsModal.error }}</div>
      </div>

      <div class="px-5 py-4 flex items-center justify-end gap-2">
        <button type="button" @click="closeSaveDeboneControlsModal" class="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors">Cancel</button>
        <button
          type="button"
          @click="confirmSaveDeboneControls"
          :disabled="saving"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all disabled:opacity-60"
        >
          {{ saving ? 'Saving...' : 'Confirm' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue'
import store from '../../store'
import { apiFetch } from '../../utils/api'

const NO_DEPARTMENT = 'none'

const NO_DEPARTMENT_SEND_TO = {
  id: 'none',
  name: 'No Department',
  displayLabel: 'No Department',
}

function normalizeSendToTargetId(id) {
  if (id === null || id === undefined) return 'none'
  const normalized = String(id).trim().toLowerCase()
  if (normalized === '' || normalized === 'none' || normalized === 'null' || normalized === 'no-department') {
    return 'none'
  }
  return String(id)
}

function isNoDepartmentSendToTarget(det) {
  return normalizeSendToTargetId(det?.id) === 'none'
}

const selectedDepartment = ref('')
const departments = ref([])
/** Company-wide active departments for Send To — same source as consumption Transfer Issu (D). */
const transferIssueDepartments = ref([])
const registrations = ref([])
const inventoryItems = ref([])
const isSavedData = ref(false)

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)

const activeDepartments = computed(() =>
  departments.value.filter((d) => (d.status ?? 1) === 1).sort((a, b) => a.name.localeCompare(b.name))
)

function isActiveDeboneRegistration(reg) {
  return (reg?.status ?? 1) === 1
}

const activeRegistrations = computed(() =>
  registrations.value.filter(isActiveDeboneRegistration)
)

function getRegistrationDepartmentId(reg) {
  const raw = reg.departmentId ?? reg.department_id ?? null
  if (raw === undefined || raw === null || raw === '') return null
  return String(raw)
}

const departmentFilteredRegistrations = computed(() => {
  if (!selectedDepartment.value) return []
  if (selectedDepartment.value === NO_DEPARTMENT) {
    return activeRegistrations.value.filter((r) => getRegistrationDepartmentId(r) === null)
  }
  return activeRegistrations.value.filter(
    (r) => getRegistrationDepartmentId(r) === String(selectedDepartment.value)
  )
})

function buildItemsById() {
  const map = new Map()
  for (const item of inventoryItems.value) {
    map.set(Number(item.id), item)
  }
  return map
}

function resolveItemForRegistration(reg, itemsById) {
  const byId = itemsById.get(Number(reg.itemId))
  if (byId) return byId

  const itemName = String(reg.itemName || '').trim().toLowerCase()
  if (!itemName) return null

  return (
    inventoryItems.value.find(
      (i) => String(i.itemName || '').trim().toLowerCase() === itemName
    ) || null
  )
}

function isDeboneKind(kind) {
  const k = String(kind || '').toLowerCase()
  return k === 'debone' || k === 'product'
}

function mergeIngredientEntry(existing, entry) {
  if (!existing) return { ...entry }
  if (entry.kind === 'item' && isDeboneKind(existing.kind)) {
    return { ...entry }
  }
  if (existing.kind === 'item') {
    return { ...existing }
  }
  return {
    ...existing,
    uom: existing.uom || entry.uom,
  }
}

/**
 * Build a unique ingredient list from every active debone_registration row:
 * - all Deboni Item Names (parent items)
 * - all Deboni Product Names (deboni names)
 * Names are de-duplicated case-insensitively; item rows take precedence on collision.
 */
function buildUniqueIngredientList(activeRegs, itemsById) {
  const byName = new Map()

  for (const reg of activeRegs) {
    const item = resolveItemForRegistration(reg, itemsById)
    const itemName = String(reg.itemName || item?.itemName || '').trim()
    const productName = String(reg.productName || reg.deboniName || '').trim()

    if (itemName) {
      const itemKey = itemName.toLowerCase()
      const itemEntry = {
        name: itemName,
        uom: item?.unit || '',
        kind: 'item',
        itemId: reg.itemId ?? item?.id ?? null,
      }
      byName.set(itemKey, mergeIngredientEntry(byName.get(itemKey), itemEntry))
    }

    if (productName) {
      const productKey = productName.toLowerCase()
      const productEntry = {
        name: productName,
        uom: reg.uom || '',
        kind: 'debone',
        registrationId: reg.id ?? null,
      }
      byName.set(productKey, mergeIngredientEntry(byName.get(productKey), productEntry))
    }
  }

  return Array.from(byName.values()).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  )
}

function normalizeUnit(unit) {
  return (unit || '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

function isLiterOrKgUnit(unit) {
  const unitNorm = normalizeUnit(unit)
  return (
    unitNorm === 'l' ||
    unitNorm === 'liter' ||
    unitNorm === 'liters' ||
    unitNorm.startsWith('liter') ||
    unitNorm === 'kg' ||
    unitNorm === 'kilogram'
  )
}

function isPiecesUomValue(uom) {
  const unitNorm = normalizeUnit(uom)
  return unitNorm === 'pieces' || unitNorm === 'pc' || uom === 'Pieces' || uom === 'Pc'
}

function isQtyPcEnabled(row) {
  return row.kind === 'item' || row.usesPc
}

function isQtyKgLEnabled(row) {
  return row.kind === 'item' || row.usesKgL
}

function rowQty(row, field, unit) {
  const key = unit === 'pc' ? `${field}Pc` : `${field}KgL`
  return Number(row[key]) || 0
}

/** Product in Total On Hand: subtract for items, add for debone products (same as consumptionPage). */
function productContributionForTotalOnHand(row, unit) {
  const prod = rowQty(row, 'product', unit)
  return row.kind === 'item' ? -prod : prod
}

/** Split SIV / non-stock receipt qty into PC vs Kg/L based on row UOM (same source as consumptionPage). */
function splitReceiptQty(value, row) {
  const n = Number(value) || 0
  if (!n) return { pc: 0, kgl: 0 }
  if (row.kind === 'item') {
    if (isPiecesUomValue(row.uom)) return { pc: n, kgl: 0 }
    if (isLiterOrKgUnit(row.uom) || row.uom === 'Kg' || row.uom === 'Ltr') return { pc: 0, kgl: n }
    return { pc: n, kgl: 0 }
  }
  if (isDeboneKind(row.kind)) {
    if (row.usesKgL && !row.usesPc) return { pc: 0, kgl: n }
    return { pc: n, kgl: 0 }
  }
  return { pc: n, kgl: 0 }
}

function inventoryDepartmentQueryValue() {
  if (selectedDepartment.value === NO_DEPARTMENT) return NO_DEPARTMENT
  return selectedDepartment.value
}

function buildControlRowKey(row) {
  if (row.kind === 'item' && row.itemId) return `item_${row.itemId}`
  if (isDeboneKind(row.kind) && row.deboneRegistrationId) return `debone_${row.deboneRegistrationId}`
  const name = String(row.ingredientName || '').trim().toLowerCase()
  const kindPrefix = isDeboneKind(row.kind) ? 'debone' : row.kind
  return `${kindPrefix}_name_${name}`
}

function getSavedDeboneRowKey(savedRow) {
  const type = String(savedRow?.type || savedRow?.kind || 'item').toLowerCase()
  if (type === 'item' && savedRow?.itemId) return `item_${savedRow.itemId}`
  if ((type === 'debone' || type === 'product') && savedRow?.deboneRegistrationId) {
    return `debone_${savedRow.deboneRegistrationId}`
  }
  return buildControlRowKey(savedRow)
}

function mapSavedSendToDetails(details) {
  if (!Array.isArray(details)) return []
  return details.map((d) => ({
    ...d,
    qtyPc: Number(d.qtyPc ?? d.quantity ?? 0) || 0,
    qtyKgL: Number(d.qtyKgL ?? 0) || 0,
    _savedQtyPc: Number(d.qtyPc ?? d.quantity ?? 0) || 0,
    _savedQtyKgL: Number(d.qtyKgL ?? 0) || 0,
  }))
}

/** Ensure saved API rows have display fields (usesPc/usesKgL) like live rows. */
function enrichSavedRowShape(savedRow) {
  const kind = isDeboneKind(savedRow?.kind || savedRow?.type) ? 'debone' : 'item'
  const uom = savedRow?.uom || null
  const usesPc = isPiecesUomValue(uom)
  const usesKgL = isLiterOrKgUnit(uom) || uom === 'Kg' || uom === 'Ltr'
  return {
    ...savedRow,
    kind,
    ingredientName: savedRow?.ingredientName || savedRow?.ingredient || '',
    uom,
    usesPc,
    usesKgL,
    deboneControlId: savedRow?.id ?? savedRow?.deboneControlId ?? null,
  }
}

/**
 * Saved snapshot behavior (same pattern as consumptionPage):
 * - Iterate saved rows; overlay live source columns (bbf, receipts, product, transfers)
 * - Keep manual/editable fields from saved record
 * - Product PC/Kg/L always from live calculation
 */
function mergeSavedDeboneRows(liveRows, savedRows) {
  const liveMap = new Map()
  ;(Array.isArray(liveRows) ? liveRows : []).forEach((liveRow) => {
    liveMap.set(buildControlRowKey(liveRow), liveRow)
  })

  return (Array.isArray(savedRows) ? savedRows : [])
    .map((savedRow) => {
      const savedKey = getSavedDeboneRowKey(savedRow)
      const liveRow = liveMap.get(savedKey)

      if (!liveRow) {
        return {
          ...enrichSavedRowShape(savedRow),
          productPc: 0,
          productKgL: 0,
          sendToDetails: mapSavedSendToDetails(savedRow.sendToDetails),
        }
      }

      const liveTransInDetails = Array.isArray(liveRow.transInDetails) ? liveRow.transInDetails : []
      return {
        ...liveRow,
        id: savedRow.id ?? savedRow.deboneControlId ?? liveRow.id,
        deboneControlId: savedRow.id ?? savedRow.deboneControlId ?? liveRow.deboneControlId,
        transInDetails: liveTransInDetails.length
          ? liveTransInDetails
          : (Array.isArray(savedRow.transInDetails) ? savedRow.transInDetails : []),
        transIn1Pc: liveRow.transIn1Pc ?? Number(savedRow.transIn1Pc ?? 0),
        transIn1KgL: liveRow.transIn1KgL ?? Number(savedRow.transIn1KgL ?? 0),
        wastagePc: Number(savedRow.wastagePc ?? 0),
        wastageKgL: Number(savedRow.wastageKgL ?? 0),
        spoiledItemPc: Number(savedRow.spoiledItemPc ?? 0),
        spoiledItemKgL: Number(savedRow.spoiledItemKgL ?? 0),
        cateringPc: Number(savedRow.cateringPc ?? 0),
        cateringKgL: Number(savedRow.cateringKgL ?? 0),
        productPc: Number(liveRow.productPc ?? 0),
        productKgL: Number(liveRow.productKgL ?? 0),
        sendToDetails: Array.isArray(savedRow.sendToDetails)
          ? mapSavedSendToDetails(savedRow.sendToDetails)
          : liveRow.sendToDetails,
      }
    })
    .sort((a, b) =>
      String(a.ingredientName || '').localeCompare(String(b.ingredientName || ''), undefined, {
        sensitivity: 'base',
      })
    )
}

function mapRowToSavePayload(row) {
  const calculated = calculateRowTotals({ ...row }, true)
  const kind = isDeboneKind(calculated.kind) ? 'debone' : 'item'
  return {
    type: kind,
    kind,
    itemId: kind === 'item' ? calculated.itemId ?? null : null,
    deboneRegistrationId: kind === 'debone' ? calculated.deboneRegistrationId ?? null : null,
    ingredientName: calculated.ingredientName,
    uom: calculated.uom,
    bbfPc: Number(calculated.bbfPc) || 0,
    bbfKgL: Number(calculated.bbfKgL) || 0,
    storeRecvPc: Number(calculated.storeRecvPc) || 0,
    storeRecvKgL: Number(calculated.storeRecvKgL) || 0,
    nonStockPc: Number(calculated.nonStockPc) || 0,
    nonStockKgL: Number(calculated.nonStockKgL) || 0,
    productPc: Number(calculated.productPc) || 0,
    productKgL: Number(calculated.productKgL) || 0,
    transIn1Pc: Number(calculated.transIn1Pc) || 0,
    transIn1KgL: Number(calculated.transIn1KgL) || 0,
    transInDetails: (calculated.transInDetails || []).map((d) => ({
      name: d.name,
      quantity: Number(d.quantity) || 0,
    })),
    wastagePc: Number(calculated.wastagePc) || 0,
    wastageKgL: Number(calculated.wastageKgL) || 0,
    totalOnHandPc: Number(calculated.totalOnHandPc) || 0,
    totalOnHandKgL: Number(calculated.totalOnHandKgL) || 0,
    sendToDetails: (calculated.sendToDetails || []).map((d) => {
      const targetId = normalizeSendToTargetId(d.id)
      return {
        id: targetId === 'none' ? 'none' : d.id,
        name: d.name || (targetId === 'none' ? NO_DEPARTMENT_SEND_TO.name : ''),
        displayLabel: d.displayLabel || d.name || (targetId === 'none' ? NO_DEPARTMENT_SEND_TO.displayLabel : ''),
        qtyPc: Number(d.qtyPc) || 0,
        qtyKgL: Number(d.qtyKgL) || 0,
        is_confirmed: d.is_confirmed ?? 0,
      }
    }),
    totalDistributedPc: Number(calculated.totalDistributedPc) || 0,
    totalDistributedKgL: Number(calculated.totalDistributedKgL) || 0,
    spoiledItemPc: Number(calculated.spoiledItemPc) || 0,
    spoiledItemKgL: Number(calculated.spoiledItemKgL) || 0,
    cateringPc: Number(calculated.cateringPc) || 0,
    cateringKgL: Number(calculated.cateringKgL) || 0,
    totalActivePc: Number(calculated.totalActivePc) || 0,
    totalActiveKgL: Number(calculated.totalActiveKgL) || 0,
    balancePc: Number(calculated.balancePc) || 0,
    balanceKgL: Number(calculated.balanceKgL) || 0,
    endingPc: Number(calculated.endingPc) || 0,
    endingKgL: Number(calculated.endingKgL) || 0,
    variancePc: Number(calculated.variancePc) || 0,
    varianceKgL: Number(calculated.varianceKgL) || 0,
  }
}

function applyInventorySourcesToRows(controlRows, sources = {}, receipts = {}, products = {}) {
  for (const row of controlRows) {
    const key = buildControlRowKey(row)
    const src = sources[key] || {}
    const rcpt = receipts[key] || {}
    const prod = products[key] || {}
    row.bbfPc = Number(src?.bbfPc ?? 0)
    row.bbfKgL = Number(src?.bbfKgL ?? 0)
    row.endingPc = Number(src?.endingPc ?? 0)
    row.endingKgL = Number(src?.endingKgL ?? 0)
    const storeSplit = splitReceiptQty(rcpt.storeRecv ?? 0, row)
    const nsSplit = splitReceiptQty(rcpt.nonStock ?? 0, row)
    row.storeRecvPc = storeSplit.pc
    row.storeRecvKgL = storeSplit.kgl
    row.nonStockPc = nsSplit.pc
    row.nonStockKgL = nsSplit.kgl
    row.productPc = Number(prod?.productPc ?? 0)
    row.productKgL = Number(prod?.productKgL ?? 0)
    calculateRowTotals(row)
  }
}

async function fetchDeboneControlSources() {
  const res = await apiFetch(
    `/debones/controls/sources?departmentId=${encodeURIComponent(inventoryDepartmentQueryValue())}&date=${currentDate.value}`
  )
  if (!res.ok) return { sources: {}, receipts: {}, products: {} }
  const payload = await res.json().catch(() => ({}))
  return {
    sources: payload?.sources && typeof payload.sources === 'object' ? payload.sources : {},
    receipts: payload?.receipts && typeof payload.receipts === 'object' ? payload.receipts : {},
    products: payload?.products && typeof payload.products === 'object' ? payload.products : {},
  }
}

async function fetchDeboneControlTransfers() {
  const res = await apiFetch(
    `/debones/controls/transfers?departmentId=${encodeURIComponent(inventoryDepartmentQueryValue())}&date=${currentDate.value}`
  )
  if (!res.ok) return {}
  const payload = await res.json().catch(() => ({}))
  return payload?.rows && typeof payload.rows === 'object' ? payload.rows : {}
}

function applyTransferSourcesToRows(controlRows, transfersByKey = {}) {
  for (const row of controlRows) {
    const key = buildControlRowKey(row)
    const live = transfersByKey[key] || {}
    const liveInDetails = Array.isArray(live.transInDetails) ? live.transInDetails : []
    if (liveInDetails.length) {
      row.transInDetails = liveInDetails.map((d) => ({
        ...d,
        qtyPc: Number(d.qtyPc ?? d.quantity ?? 0) || 0,
        qtyKgL: Number(d.qtyKgL ?? 0) || 0,
      }))
    } else {
      row.transInDetails = Array.isArray(row.transInDetails) ? row.transInDetails : []
    }
    updateTransInSumForRow(row)
  }
}

const currentDate = ref('')
const todayDate = ref(new Date().toISOString().split('T')[0])
const rows = ref([])
const loading = ref(false)
const saving = ref(false)
const saveStatus = ref('')
const activePopoverIndex = ref(-1)
const activePopoverKind = ref(null)
const popoverStyle = ref({})
const popoverBodyMaxHeight = ref('240px')

const isFutureDate = computed(() => {
  if (!currentDate.value) return false
  return currentDate.value > todayDate.value
})

function applyPendingEditPermissions(rows, pendingList) {
  const list = Array.isArray(pendingList) ? pendingList : []
  return rows.map((row) => {
    const rid = row.deboneControlId ?? row.id
    const relevant = list.filter((p) => Number(p.deboneControlId) === Number(rid))
    return {
      ...row,
      canEditWastagePc: relevant.some((p) => p.wastagePc === true),
      canEditWastageKgL: relevant.some((p) => p.wastageKgL === true),
      canEditSpoiledItemPc: relevant.some((p) => p.spoiledItemPc === true),
      canEditSpoiledItemKgL: relevant.some((p) => p.spoiledItemKgL === true),
      canEditCateringPc: relevant.some((p) => p.cateringPc === true),
      canEditCateringKgL: relevant.some((p) => p.cateringKgL === true),
      canEditSendToD: relevant.some((p) => p.sendToD === true),
    }
  })
}

/** Saved rows: only `debone_control_edit_access` flags matter. Live draft (no saved data): editable for today and past dates. */
function deboneControlFieldDisabled(row, field) {
  if (isFutureDate.value) return true
  if (!currentDate.value || !selectedDepartment.value) return true
  const key =
    field === 'wastagePc'
      ? 'canEditWastagePc'
      : field === 'wastageKgL'
        ? 'canEditWastageKgL'
        : field === 'spoiledItemPc'
          ? 'canEditSpoiledItemPc'
          : field === 'spoiledItemKgL'
            ? 'canEditSpoiledItemKgL'
            : field === 'cateringPc'
              ? 'canEditCateringPc'
              : field === 'cateringKgL'
                ? 'canEditCateringKgL'
                : field === 'sendToD' || field === 'qtyPc' || field === 'qtyKgL'
                  ? 'canEditSendToD'
                  : null
  if (!key) return true
  if (
    (field === 'wastagePc' || field === 'spoiledItemPc' || field === 'cateringPc' || field === 'qtyPc') &&
    !isQtyPcEnabled(row)
  ) {
    return true
  }
  if (
    (field === 'wastageKgL' || field === 'spoiledItemKgL' || field === 'cateringKgL' || field === 'qtyKgL') &&
    !isQtyKgLEnabled(row)
  ) {
    return true
  }
  if (isSavedData.value) return !row[key]
  return false
}

const sendToPopoverTotalPc = computed(() => {
  const row = rows.value[activePopoverIndex.value]
  if (!row) return 0
  return (row.sendToDetails || []).reduce((sum, det) => sum + (Number(det?.qtyPc) || 0), 0)
})

const sendToPopoverTotalKgL = computed(() => {
  const row = rows.value[activePopoverIndex.value]
  if (!row) return 0
  return (row.sendToDetails || []).reduce((sum, det) => sum + (Number(det?.qtyKgL) || 0), 0)
})

/** Live draft: any date except future. Saved: pending edit-access still enforced per field. */
const canClickSaveChanges = computed(() => {
  if (saving.value) return false
  if (!currentDate.value || !selectedDepartment.value) return false
  if (isFutureDate.value) return false
  if (rows.value.length === 0) return false
  return true
})

const saveDeboneControlsModal = ref({
  open: false,
  code: '',
  input: '',
  error: '',
})

function generateVerificationCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let out = ''
  for (let i = 0; i < 4; i++) {
    out += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return out
}

function openSaveDeboneControlsModal() {
  if (!canClickSaveChanges.value) return
  saveDeboneControlsModal.value.open = true
  saveDeboneControlsModal.value.code = generateVerificationCode()
  saveDeboneControlsModal.value.input = ''
  saveDeboneControlsModal.value.error = ''
}

function closeSaveDeboneControlsModal() {
  if (saving.value) return
  saveDeboneControlsModal.value.open = false
}

async function confirmSaveDeboneControls() {
  const expected = String(saveDeboneControlsModal.value.code || '').trim()
  const entered = String(saveDeboneControlsModal.value.input || '').trim().toUpperCase()
  if (!entered || entered !== expected) {
    saveDeboneControlsModal.value.error = 'Invalid code. Please enter the exact 4-character code.'
    return
  }
  saveDeboneControlsModal.value.error = ''
  const ok = await saveAllRows()
  if (ok) {
    saveDeboneControlsModal.value.open = false
  }
}

function displayNum(val) {
  const num = Number(val || 0)
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 5 })
}

function hasSendToQuantity(row) {
  return (row?.sendToDetails || []).some(
    (det) => (Number(det?.qtyPc) || 0) > 0 || (Number(det?.qtyKgL) || 0) > 0
  )
}

/** Red "Edit" when any issued qty is not confirmed (same rule as consumption Transfer Issu D). */
function hasPendingSendTo(row) {
  return (row?.sendToDetails || []).some((det) => {
    const qtyPc = Number(det?.qtyPc) || 0
    const qtyKgL = Number(det?.qtyKgL) || 0
    if (qtyPc <= 0 && qtyKgL <= 0) return false
    return Number(det?.is_confirmed ?? 0) !== 1
  })
}

function sendToDetailsForPopover(row) {
  if (!row) return []
  const selfId =
    selectedDepartment.value === NO_DEPARTMENT ? '' : String(selectedDepartment.value || '')
  return (row.sendToDetails || []).filter((d) => {
    const targetId = normalizeSendToTargetId(d.id)
    if (selfId && targetId === selfId) return false
    if (isNoDepartmentSendToTarget(d)) {
      return selectedDepartment.value !== NO_DEPARTMENT
    }
    return transferIssueDepartments.value.some((dep) => String(dep.id) === targetId)
  })
}

function syncSendToDetailsWithCompanyDepartments() {
  const selfId =
    selectedDepartment.value === NO_DEPARTMENT ? '' : String(selectedDepartment.value || '')
  const allowedIds = new Set(transferIssueDepartments.value.map((d) => String(d.id)))
  if (selectedDepartment.value !== NO_DEPARTMENT) {
    allowedIds.add('none')
  }

  if (!transferIssueDepartments.value.length && selectedDepartment.value === NO_DEPARTMENT) return

  rows.value.forEach((row) => {
    const existing = Array.isArray(row.sendToDetails) ? row.sendToDetails : []
    const byId = new Map()
    existing.forEach((d) => {
      const key = normalizeSendToTargetId(d.id)
      byId.set(key, {
        ...d,
        id: key === 'none' ? 'none' : d.id,
        qtyPc: Number(d.qtyPc ?? d.quantity ?? 0) || 0,
        qtyKgL: Number(d.qtyKgL ?? 0) || 0,
      })
    })

    for (const dept of transferIssueDepartments.value) {
      const id = String(dept.id)
      if (selfId && id === selfId) continue
      if (!byId.has(id)) {
        byId.set(id, {
          id: dept.id,
          name: dept.name,
          displayLabel: dept.displayLabel,
          qtyPc: 0,
          qtyKgL: 0,
          is_confirmed: 0,
        })
      } else {
        const entry = byId.get(id)
        entry.displayLabel = dept.displayLabel
        entry.name = dept.name
      }
    }

    if (selectedDepartment.value !== NO_DEPARTMENT && !byId.has('none')) {
      byId.set('none', {
        ...NO_DEPARTMENT_SEND_TO,
        qtyPc: 0,
        qtyKgL: 0,
        is_confirmed: 0,
      })
    }

    row.sendToDetails = Array.from(byId.values()).filter((d) => {
      const targetId = normalizeSendToTargetId(d.id)
      if (selfId && targetId === selfId) return false
      return allowedIds.has(targetId)
    })
  })
}

function updateSendToSumForRow(row) {
  if (!row) return
  const details = row.sendToDetails || []
  row.totalDistributedPc = Number(
    details.reduce((sum, det) => sum + (Number(det?.qtyPc) || 0), 0).toFixed(5)
  )
  row.totalDistributedKgL = Number(
    details.reduce((sum, det) => sum + (Number(det?.qtyKgL) || 0), 0).toFixed(5)
  )
}

async function fetchTransferIssueDepartments() {
  if (!companyId.value) {
    transferIssueDepartments.value = []
    return
  }
  try {
    const res = await apiFetch(`/departments/company-transfer-departments/${companyId.value}`)
    if (!res.ok) return
    const data = await res.json().catch(() => ({}))
    const list = Array.isArray(data?.departments) ? data.departments : []
    transferIssueDepartments.value = list.sort((a, b) =>
      String(a.displayLabel || a.name).localeCompare(String(b.displayLabel || b.name))
    )
    syncSendToDetailsWithCompanyDepartments()
  } catch (err) {
    console.error('Error fetching transfer issue departments:', err)
    transferIssueDepartments.value = []
  }
}

function mapDeboneControlRow(entry) {
  const usesPc = isPiecesUomValue(entry.uom)
  const usesKgL = isLiterOrKgUnit(entry.uom) || entry.uom === 'Kg' || entry.uom === 'Ltr'
  const row = {
    rowKey: `${entry.kind}:${entry.name}`,
    ingredientName: entry.name,
    kind: entry.kind,
    itemId: entry.itemId ?? null,
    deboneRegistrationId: entry.registrationId ?? null,
    uom: entry.uom || null,
    usesPc,
    usesKgL,
    transInDetails: [],
    transIn1Pc: 0,
    transIn1KgL: 0,
    transIn2: 0,
    sendToDetails: [],
    transOut2: 0,
    wastagePc: 0,
    wastageKgL: 0,
    spoiledItemPc: 0,
    spoiledItemKgL: 0,
    cateringPc: 0,
    cateringKgL: 0,
    bbfPc: 0,
    bbfKgL: 0,
    storeRecvPc: 0,
    storeRecvKgL: 0,
    nonStockPc: 0,
    nonStockKgL: 0,
    productPc: 0,
    productKgL: 0,
    endingPc: 0,
    endingKgL: 0,
    totalOnHandPc: 0,
    totalOnHandKgL: 0,
    totalDistributedPc: 0,
    totalDistributedKgL: 0,
    totalActivePc: 0,
    totalActiveKgL: 0,
    balancePc: 0,
    balanceKgL: 0,
    variancePc: 0,
    varianceKgL: 0,
  }
  return calculateRowTotals(row)
}

function updateTransInSumForRow(row) {
  if (!row) return
  const details = row.transInDetails || []
  row.transIn1Pc = Number(
    details
      .reduce(
        (sum, det) =>
          sum + (det.is_confirmed === 1 ? Number(det.qtyPc ?? det.quantity) || 0 : 0),
        0
      )
      .toFixed(5)
  )
  row.transIn1KgL = Number(
    details
      .reduce(
        (sum, det) => sum + (det.is_confirmed === 1 ? Number(det.qtyKgL) || 0 : 0),
        0
      )
      .toFixed(5)
  )
}

function calculateRowTotals(row, force = false) {
  if (!row) return row
  if (isSavedData.value && !force) return row

  // Total On Hand: BBF + receipts + non-stock ± product (subtract if item) + transfers in - wastage
  row.totalOnHandPc = (
    rowQty(row, 'bbf', 'pc') +
    rowQty(row, 'storeRecv', 'pc') +
    rowQty(row, 'nonStock', 'pc') +
    productContributionForTotalOnHand(row, 'pc') +
    rowQty(row, 'transIn1', 'pc') -
    rowQty(row, 'wastage', 'pc')
  ).toFixed(2)

  row.totalOnHandKgL = (
    rowQty(row, 'bbf', 'kgl') +
    rowQty(row, 'storeRecv', 'kgl') +
    rowQty(row, 'nonStock', 'kgl') +
    productContributionForTotalOnHand(row, 'kgl') +
    rowQty(row, 'transIn1', 'kgl') -
    rowQty(row, 'wastage', 'kgl')
  ).toFixed(2)

  updateSendToSumForRow(row)

  row.totalActivePc = (
    rowQty(row, 'totalDistributed', 'pc') +
    rowQty(row, 'spoiledItem', 'pc') +
    rowQty(row, 'catering', 'pc')
  ).toFixed(2)

  row.totalActiveKgL = (
    rowQty(row, 'totalDistributed', 'kgl') +
    rowQty(row, 'spoiledItem', 'kgl') +
    rowQty(row, 'catering', 'kgl')
  ).toFixed(2)

  row.balancePc = (Number(row.totalOnHandPc) - Number(row.totalActivePc)).toFixed(2)
  row.balanceKgL = (Number(row.totalOnHandKgL) - Number(row.totalActiveKgL)).toFixed(2)
  row.variancePc = (rowQty(row, 'ending', 'pc') - Number(row.balancePc)).toFixed(2)
  row.varianceKgL = (rowQty(row, 'ending', 'kgl') - Number(row.balanceKgL)).toFixed(2)
  return row
}

async function recalculateRowAt(index) {
  await nextTick()
  const row = rows.value[index]
  if (!row) return
  calculateRowTotals(row, isSavedData.value)
}

async function handleConfirmTransfer(rowIndex, detIndex, status) {
  const row = rows.value[rowIndex]
  if (!row) return
  const det = row.transInDetails?.[detIndex]
  if (!det || !det.deboneControlId) return

  const receiverDepId = normalizeSendToTargetId(det.receiverDepId ?? inventoryDepartmentQueryValue())

  try {
    const res = await apiFetch('/debones/controls/confirm-transfer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        deboneControlId: det.deboneControlId,
        receiverDepId,
        is_confirmed: status,
      }),
    })

    if (res.ok) {
      det.is_confirmed = status
      updateTransInSumForRow(row)
      calculateRowTotals(row, true)
    } else {
      console.error('Failed to update debone transfer status')
    }
  } catch (err) {
    console.error('Error confirming debone transfer:', err)
  }
}

function buildSampleRows() {
  const uniqueEntries = buildUniqueIngredientList(
    departmentFilteredRegistrations.value,
    buildItemsById()
  )
  return uniqueEntries.map((entry) => mapDeboneControlRow(entry))
}

async function loadDepartments() {
  if (!companyId.value) return
  let endpoint = `/departments/company/${companyId.value}`
  if (branchId.value) {
    endpoint = `/departments/branch/${branchId.value}`
  }
  const response = await apiFetch(endpoint, { method: 'GET' })
  if (!response.ok) return
  const data = await response.json().catch(() => [])
  departments.value = Array.isArray(data) ? data : []
}

async function loadItems() {
  const params = new URLSearchParams()
  if (branchId.value) params.append('branchId', branchId.value)
  else if (companyId.value) params.append('companyId', companyId.value)
  else return

  const response = await apiFetch(`/items?${params.toString()}`)
  if (!response.ok) return
  const data = await response.json().catch(() => [])
  inventoryItems.value = Array.isArray(data) ? data : []
}

async function loadRegistrations() {
  const response = await apiFetch('/debones/registrations', { method: 'GET' })
  if (!response.ok) {
    registrations.value = []
    return
  }
  const data = await response.json().catch(() => [])
  registrations.value = (Array.isArray(data) ? data : []).filter(isActiveDeboneRegistration)
}

async function loadRows() {
  if (!currentDate.value || !selectedDepartment.value) {
    rows.value = []
    isSavedData.value = false
    return
  }

  loading.value = true
  activePopoverIndex.value = -1
  activePopoverKind.value = null
  try {
    await fetchTransferIssueDepartments()
    let builtRows = buildSampleRows()
    const { sources, receipts, products } = await fetchDeboneControlSources()
    applyInventorySourcesToRows(builtRows, sources, receipts, products)

    const transfersByKey = await fetchDeboneControlTransfers()
    applyTransferSourcesToRows(builtRows, transfersByKey)

    let pendingEditAccess = []
    if (!isFutureDate.value) {
      const savedRes = await apiFetch(
        `/debones/controls/saved?departmentId=${encodeURIComponent(inventoryDepartmentQueryValue())}&calendar=${currentDate.value}`
      )
      if (savedRes.ok) {
        const savedPayload = await savedRes.json().catch(() => ({ hasData: false, rows: [], pendingEditAccess: [] }))
        pendingEditAccess = Array.isArray(savedPayload?.pendingEditAccess) ? savedPayload.pendingEditAccess : []
        if (savedPayload?.hasData && Array.isArray(savedPayload.rows) && savedPayload.rows.length > 0) {
          // Saved snapshot: merge saved rows with live sources (product PC/Kg/L from live).
          builtRows = mergeSavedDeboneRows(builtRows, savedPayload.rows)
          isSavedData.value = true
        } else {
          isSavedData.value = false
        }
      } else {
        isSavedData.value = false
      }
    } else {
      isSavedData.value = false
    }

    rows.value = isSavedData.value
      ? applyPendingEditPermissions(builtRows, pendingEditAccess)
      : builtRows
    syncSendToDetailsWithCompanyDepartments()
    rows.value.forEach((row) => calculateRowTotals(row, true))
  } catch (err) {
    console.error('Error loading debone controls:', err)
    rows.value = buildSampleRows()
    isSavedData.value = false
    syncSendToDetailsWithCompanyDepartments()
  } finally {
    loading.value = false
  }
}

function openPopoverAt(event, index, kind) {
  if (isFutureDate.value) return
  if (kind === 'sendTo') {
    if (!currentDate.value) return
  } else if (!currentDate.value || !selectedDepartment.value) {
    return
  }
  if (activePopoverIndex.value === index && activePopoverKind.value === kind) {
    activePopoverIndex.value = -1
    activePopoverKind.value = null
    return
  }

  const rect = event.currentTarget.getBoundingClientRect()
  const panelWidth = kind === 'sendTo' ? 420 : 380
  let left = rect.left
  if (left + panelWidth > window.innerWidth - 8) {
    left = window.innerWidth - panelWidth - 8
  }

  const edgeGap = 8
  const headerHeight = 44
  const spaceBelow = Math.max(120, Math.floor(window.innerHeight - rect.bottom - edgeGap))
  const spaceAbove = Math.max(120, Math.floor(rect.top - edgeGap))
  const openUp = spaceBelow < 260 && spaceAbove > spaceBelow

  let verticalStyle = {}
  let bodyMax = 240
  if (openUp) {
    verticalStyle = { bottom: `${window.innerHeight - rect.top + 2}px` }
    bodyMax = Math.max(120, spaceAbove - headerHeight)
  } else {
    verticalStyle = { top: `${rect.bottom + 2}px` }
    bodyMax = Math.max(120, spaceBelow - headerHeight)
  }
  popoverBodyMaxHeight.value = `${bodyMax}px`
  popoverStyle.value = { ...verticalStyle, left: `${left}px`, minWidth: `${panelWidth}px` }
  activePopoverIndex.value = index
  activePopoverKind.value = kind
}

function openTransInPopover(event, index) {
  openPopoverAt(event, index, 'transIn')
}

function openSendToPopover(event, index) {
  openPopoverAt(event, index, 'sendTo')
}

function updateSendToSum(index) {
  const row = rows.value[index]
  if (!row) return
  updateSendToSumForRow(row)
  calculateRowTotals(row)
}

const closePopovers = () => {
  activePopoverIndex.value = -1
  activePopoverKind.value = null
}

async function saveAllRows() {
  if (!selectedDepartment.value || !currentDate.value || rows.value.length === 0) return false
  if (isFutureDate.value) return false
  saving.value = true
  saveStatus.value = ''
  try {
    const payloadRows = rows.value.map((row) => mapRowToSavePayload(row))
    const res = await apiFetch('/debones/controls/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        departmentId: inventoryDepartmentQueryValue(),
        calendar: currentDate.value,
        rows: payloadRows,
      }),
    })

    saveStatus.value = res.ok ? 'success' : 'error'
    if (res.ok) {
      await loadRows()
      return true
    }
    return false
  } catch (err) {
    console.error('Error saving debone controls:', err)
    saveStatus.value = 'error'
    return false
  } finally {
    saving.value = false
    setTimeout(() => {
      saveStatus.value = ''
    }, 3000)
  }
}

onMounted(async () => {
  loading.value = true
  try {
    await Promise.all([loadDepartments(), loadItems(), loadRegistrations(), fetchTransferIssueDepartments()])
    if (selectedDepartment.value && currentDate.value) {
      await loadRows()
    }
  } finally {
    loading.value = false
  }
  window.addEventListener('click', closePopovers)
})

onUnmounted(() => {
  window.removeEventListener('click', closePopovers)
})
</script>

<style scoped>
.debone-count-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: collapse;
}

.debone-count-table th,
.debone-count-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.debone-count-table th:nth-child(1),
.debone-count-table td:nth-child(1) {
  text-align: center;
}

.debone-count-table thead th.col-ingredient-name,
.debone-count-table td.col-ingredient-name {
  text-align: left;
}

.debone-count-table th:not(:nth-child(2)),
.debone-count-table td:not(:nth-child(2)) {
  text-align: center;
}

.debone-count-table thead th.qty-group-header {
  text-align: center;
}

.debone-count-table td.col-center {
  text-align: center;
}

thead tr:nth-child(2) .qty-sub-header {
  top: 33px;
  z-index: 19;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 10;
  background-clip: padding-box;
  border-right: 2px solid #e2e8f0 !important;
}

.no-side-border {
  border-right: none !important;
  border-left: none !important;
}

th.sticky-col {
  z-index: 30;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
